// In-battle voice bubble overlay. Two visual variants driven by the
// dossier's `can_speak` flag:
//
//   speaker  — rounded corners + tail. Standard comic speech bubble.
//   narrator — rectangular box, italic text, no tail. Used for stage-
//              direction lines from non-speaking entities (machines,
//              eldritch horrors, objects, animals).
//
// Positioning is anchor-driven:
//
//   - HTMLElement anchor: bubble appears above-and-centered on the
//     element, tail pointing down at it. Used for character cards in
//     the ring and for the in-flight end-game interstitial cards.
//   - 'opponent-header' / 'player-header': bubble descends from below
//     the corresponding profile icon, can span the top of the screen.
//     Used for opponent_intro and (in the interstitial) the opponent
//     win/lose lines.
//
// One bubble per anchor at a time — calling showVoiceBubble again with
// the same anchor while a previous bubble is still on screen replaces
// it with a quick crossfade. The renderer enforces higher-level
// throttling (combat pair per turn, etc.) before calling.

import { getConfig } from '../config.js';

export type VoiceBubbleVariant = 'speaker' | 'narrator';

export type VoiceBubbleAnchor =
  | HTMLElement                  // tracks the element's rect via rAF
  | DOMRect                      // static rect (no tracking, no rAF) — used for cards already removed from DOM
  | 'opponent-header'
  | 'player-header';

export interface VoiceBubbleOptions {
  text: string;
  variant: VoiceBubbleVariant;
  anchor: VoiceBubbleAnchor;
  /** Override dwell time (ms visible after fade-in). Defaults to
   *  `config.voiceLineDwellMs`. */
  dwellMs?: number;
  /** Override fade-in / fade-out duration. Defaults to
   *  `config.voiceLineFadeMs`. */
  fadeMs?: number;
  /** Delay before the bubble starts fading in. Used by the renderer
   *  for `die` lines (so they appear after the killing-blow animation
   *  instead of simultaneously with the kill_other line). Defaults
   *  to 0. */
  delayMs?: number;
  /** Optional callback fired when the bubble is fully dismissed
   *  (after fade-out completes). Used by the interstitial state
   *  machine to chain scenes. */
  onDismissed?: () => void;
}

/** Active per-anchor bubble registry — used to evict a prior bubble
 *  when a new one targets the same anchor. Anchors are either DOM
 *  elements or fixed string keys for the header slots. */
const activeBubbles = new Map<VoiceBubbleAnchor, HTMLElement>();

/** Show a voice bubble. Returns a handle that can be `.dismiss()`ed
 *  early (used by the interstitial skip-on-tap). */
export function showVoiceBubble(options: VoiceBubbleOptions): { dismiss: () => void } {
  const dwellMs = options.dwellMs ?? getConfig().voiceLineDwellMs;
  const fadeMs = options.fadeMs ?? getConfig().voiceLineFadeMs;
  const delayMs = options.delayMs ?? 0;

  // Evict any prior bubble on this anchor.
  const prior = activeBubbles.get(options.anchor);
  if (prior) {
    prior.remove();
    activeBubbles.delete(options.anchor);
  }

  const bubble = document.createElement('div');
  bubble.className = `voice-bubble voice-bubble-${options.variant}`;
  bubble.style.opacity = '0';
  bubble.style.transition = `opacity ${fadeMs}ms ease`;
  bubble.textContent = options.text;

  // Side-effects (rAF subscriptions, observers, etc.) that need
  // tearing down when the bubble dismisses. Each entry is run once.
  const cleanupOnDismiss: Array<() => void> = [];

  let dismissed = false;

  // Position. Header anchors get their own absolute-positioned slots
  // attached to the body; character-card anchors attach the bubble as
  // a child of the card's positioned parent so it tracks scroll.
  if (options.anchor === 'opponent-header' || options.anchor === 'player-header') {
    bubble.classList.add(`voice-bubble-${options.anchor}`);
    document.body.appendChild(bubble);
    // Position relative to the actual profile-avatar element on screen.
    // The CSS fallback hardcodes left: 8px for the opponent slot, which
    // matches mobile portrait (where the board is flush with the
    // viewport) but lands at the screen edge on desktop, where the
    // play board has horizontal padding. Reading the avatar's
    // bounding rect lets the bubble's edge line up with the avatar
    // edge in every layout.
    const side = options.anchor === 'opponent-header' ? 'opponent' : 'player';
    const avatar = document.querySelector(
      `.player-info.${side} .player-profile-avatar`,
    ) as HTMLElement | null;
    if (avatar) {
      const rect = avatar.getBoundingClientRect();
      bubble.style.top = `${rect.bottom + 6}px`;
      if (side === 'opponent') {
        // Opponent avatar is upper-left. Align bubble's left edge
        // with the avatar's left edge; let it extend rightward.
        bubble.style.left = `${rect.left}px`;
        bubble.style.right = 'auto';
      } else {
        // Player avatar is upper-right. Align bubble's right edge.
        bubble.style.right = `${Math.max(8, window.innerWidth - rect.right)}px`;
        bubble.style.left = 'auto';
      }
    }
  } else if (options.anchor instanceof DOMRect) {
    // Static-rect anchor — the card has already left the DOM (typical
    // for non-combat deaths where render() removed the cardEl before
    // voice routing ran). Position the bubble once at the captured
    // rect and skip rAF tracking entirely.
    bubble.classList.add('voice-bubble-card');
    document.body.appendChild(bubble);
    bubble.style.position = 'fixed';
    bubble.style.transform = 'translate(-50%, -100%)';
    const rect = options.anchor;
    positionAndClampCardBubble(bubble, rect.left + rect.width / 2, rect.top - 12);
  } else {
    bubble.classList.add('voice-bubble-card');
    document.body.appendChild(bubble);
    bubble.style.position = 'fixed';
    bubble.style.transform = 'translate(-50%, -100%)';
    // Track the card's bounding rect every animation frame so the
    // bubble follows whatever transforms the card has (staging lean,
    // hit-shake, etc.). Two stop conditions:
    //
    //   1. Card removed from the DOM (e.g. discard, return-to-hand):
    //      freeze the bubble at its last live position.
    //   2. Card starts the KO death animation (.anim-ko class added):
    //      pin the bubble to the ring slot's stable rect and stop
    //      tracking. The `.anim-ko` keyframes apply `transform:
    //      scale(...)` which both overrides the .blocker / .attacker
    //      lean AND shrinks the card's bounding rect around its
    //      center — without this pin the bubble would visibly drift
    //      back DOWN as the shrinking rect's `top` migrated toward
    //      the previously-leaned position. The ring slot's rect is
    //      stable through all of this.
    const card = options.anchor;
    let frozen = false;
    const updatePosition = (): boolean => {
      if (frozen) return false;
      if (!document.body.contains(card)) return false;
      const rect = card.getBoundingClientRect();
      // Skip updates while the card is off-layout (rect all-zero) —
      // happens briefly during fade-in transitions on some browsers.
      if (rect.width === 0 && rect.height === 0) return true;
      positionAndClampCardBubble(bubble, rect.left + rect.width / 2, rect.top - 12);
      return true;
    };
    /** Pin the bubble to the card's ring-slot home position and stop
     *  tracking. The ring slot doesn't get transforms applied to it,
     *  so its rect is the stable home position the card would return
     *  to absent any combat / death animations. */
    const pinToRingSlot = (): void => {
      const slot = card.closest('.ring-slot') as HTMLElement | null;
      if (slot) {
        const rect = slot.getBoundingClientRect();
        positionAndClampCardBubble(bubble, rect.left + rect.width / 2, rect.top - 12);
      }
      frozen = true;
    };

    // Immediate pin if the death animation is ALREADY in progress
    // when the bubble fires — typical for non-combat deaths, where
    // killKOCard in main.ts adds .anim-ko to the restored card
    // during the thaw phase, BEFORE voice routing runs. The
    // MutationObserver below only catches future class additions,
    // not classes already present at observe-time.
    if (card.classList.contains('anim-ko')) {
      pinToRingSlot();
      // No rAF / observer setup needed — bubble is frozen at the
      // ring slot's home rect for the rest of its dwell.
    } else {
      updatePosition();

      // MutationObserver watches for the .anim-ko class arriving on the
      // card. Synchronous-ish microtask delivery (before the next rAF
      // tick) so the pin happens before any frame where the scale
      // transform would skew the card's rect.
      const koObserver = new MutationObserver(() => {
        if (frozen) return;
        if (card.classList.contains('anim-ko')) pinToRingSlot();
      });
      koObserver.observe(card, { attributes: true, attributeFilter: ['class'] });

      let rafId: number | null = null;
      const tick = (): void => {
        if (dismissed) return;
        const stillTracking = updatePosition();
        if (stillTracking) {
          rafId = requestAnimationFrame(tick);
        } else {
          rafId = null;
        }
      };
      rafId = requestAnimationFrame(tick);

      cleanupOnDismiss.push(() => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = null;
        koObserver.disconnect();
      });
    }
  }

  activeBubbles.set(options.anchor, bubble);

  let dwellTimer: ReturnType<typeof setTimeout> | null = null;
  let removeTimer: ReturnType<typeof setTimeout> | null = null;

  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    if (dwellTimer) clearTimeout(dwellTimer);
    for (const fn of cleanupOnDismiss) fn();
    bubble.style.opacity = '0';
    removeTimer = setTimeout(() => {
      bubble.remove();
      if (activeBubbles.get(options.anchor) === bubble) {
        activeBubbles.delete(options.anchor);
      }
      options.onDismissed?.();
    }, fadeMs);
  };

  // Fade in (after optional delay), dwell, fade out, dismiss.
  const startFadeIn = (): void => {
    // Force a layout flush so the opacity transition fires.
    void bubble.offsetWidth;
    bubble.style.opacity = '1';
    dwellTimer = setTimeout(dismiss, fadeMs + dwellMs);
  };
  if (delayMs > 0) {
    setTimeout(startFadeIn, delayMs);
  } else {
    startFadeIn();
  }

  return { dismiss };
}

/** Place a card-anchored bubble centered on (anchorCenterX, anchorBottomY)
 *  via translate(-50%, -100%), then horizontally shift it back into the
 *  viewport if either edge would clip — and re-aim the tail at the
 *  original anchor center so it still points at the speaking card.
 *
 *  Mobile portrait is the case that needs this: a card in the leftmost
 *  or rightmost ring slot is close enough to the viewport edge that a
 *  ~280-340px bubble centered on it would extend past the screen, so
 *  the first/last few words got clipped off-screen. The bubble's own
 *  max-width (via CSS) caps width below viewport so a one-axis shift
 *  is always enough to bring both edges into range. */
function positionAndClampCardBubble(
  bubble: HTMLElement,
  anchorCenterX: number,
  anchorBottomY: number,
): void {
  bubble.style.left = `${anchorCenterX}px`;
  bubble.style.top = `${anchorBottomY}px`;
  // Compute the clamp from `offsetWidth` (layout width, transform-
  // independent) and the anchor center, NOT from getBoundingClientRect
  // which reflects the previous frame's already-shifted transform.
  // Reading the post-transform rect creates a feedback loop: frame N
  // measures a shifted rect, computes a new shift, frame N+1 measures
  // the differently-shifted rect, computes a different shift, and the
  // bubble oscillates back and forth at rAF frequency. Deriving shift
  // purely from anchorCenterX + bubble width + viewport width makes
  // the result stable across frames whenever those inputs don't change.
  const margin = 8;
  const bubbleWidth = bubble.offsetWidth;
  const unshiftedLeft = anchorCenterX - bubbleWidth / 2;
  const maxLeft = window.innerWidth - margin - bubbleWidth;
  const clampedLeft = Math.max(margin, Math.min(maxLeft, unshiftedLeft));
  const shiftX = clampedLeft - unshiftedLeft;
  if (shiftX !== 0) {
    bubble.style.transform = `translate(calc(-50% + ${shiftX}px), -100%)`;
  } else {
    bubble.style.transform = 'translate(-50%, -100%)';
  }
  // Push the tail in the opposite direction so it still points at the
  // anchor's original center (consumed by .voice-bubble-card::after CSS).
  bubble.style.setProperty('--tail-x', `calc(50% - ${shiftX}px)`);
}

/** Clear all active bubbles. Called at match-end (before the
 *  interstitial takes over) and on any global teardown. */
export function clearAllVoiceBubbles(): void {
  for (const bubble of activeBubbles.values()) {
    bubble.remove();
  }
  activeBubbles.clear();
}
